import { useState, useEffect } from 'react';
import { useBooleanFlagValue, useWhenProviderReady } from '@openfeature/react-sdk';

interface UseInteractiveFlagResult {
  enabled: boolean;
  loading: boolean;
}

// Dev-only override key.
// Usage from browser console (then refresh):
//   enable:  localStorage.setItem('__interactive_enabled__', 'true')
//   disable: localStorage.removeItem('__interactive_enabled__')
const DEV_OVERRIDE_KEY = '__interactive_enabled__';

function getDevOverride(): boolean | null {
  // Overrides are strictly disabled in production builds
  if (process.env.NODE_ENV === 'production') return null;

  // 1. Check if the local dev environment explicitly enabled or disabled it
  if (process.env.INTERACTIVE_ENABLED === 'true') return true;
  if (process.env.INTERACTIVE_ENABLED === 'false') return false;

  // 2. Check local storage for local developer overrides
  const val = localStorage.getItem(DEV_OVERRIDE_KEY);
  if (val !== null) return val === 'true';

  return null;
}

export function useInteractiveFlag(): UseInteractiveFlagResult {
  const devOverride = getDevOverride();
  
  // Use OpenFeature hooks
  const value = useBooleanFlagValue('interactive_frontend', false);
  const isReady = useWhenProviderReady();

  const [enabled, setEnabled] = useState(devOverride ?? false);
  const [loading, setLoading] = useState(devOverride === null);

  useEffect(() => {
    // Dev override is active — skip the OpenFeature value entirely
    if (devOverride !== null) return;

    // `loading` covers only the first resolve. Every token refresh re-syncs the
    // OpenFeature context, which drops the provider out of READY while it
    // reconciles — going back to loading then would unmount the micro-frontend.
    if (isReady) {
      setEnabled(value);
      setLoading(false);
    }
  }, [devOverride, value, isReady]);

  return { enabled, loading };
}
